/**
 * Single source of truth for GoReal's public contact details.
 *
 * TODO(owner): the brokerage's REAL phone number, support inbox, mailing
 * address, and CA DRE license number must be supplied by the business owner
 * before launch. Until then these are intentionally `null` / owned-domain
 * placeholders so every surface renders an honest omitted state instead of a
 * fabricated `(415) 555-1234` number, a fake `CA DRE #01234567`, or a
 * `123 Market Street` office — none of which may ship on a real, regulated
 * real-estate product. Set the real values here once and the header, footer,
 * contact CTAs, JSON-LD, and llms.txt all update from this one place.
 */

/**
 * Real business phone as a US 10-digit display string, e.g. `'(415) 555-0142'`.
 * Must be a line the brokerage actually answers. `null` hides every call/text
 * CTA across the site (they fall back to email).
 * Supplied by the owner 2026-07-24 — a real, answerable line.
 */
export const CONTACT_PHONE: string | null = '(650) 667-8000';

/**
 * Support inbox. `letsgoreal.com` is the owned, email-configured domain (Postmark
 * + Cloudflare DKIM/SPF/DMARC), unlike the previously-used unowned `goreal.com`.
 * TODO(owner): confirm the `hello@` mailbox/forwarding is monitored before
 * promoting it as the primary support channel.
 */
export const CONTACT_EMAIL = 'hello@letsgoreal.com';

/**
 * Real business mailing address, or `null` to omit it entirely. We never render
 * a placeholder office.
 * TODO(owner): set the real address, or leave null.
 */
export const CONTACT_ADDRESS: { line1: string; line2: string } | null = null;

/**
 * The brokerage's CA DRE license number, supplied by the owner 2026-07-25.
 *
 * California requires a licensee to display this on advertising, and the site
 * asserts licensure in several places (about, homepage, pricing) — so the number
 * and the claim have to travel together. One constant drives all of them: the
 * site-wide footer, /about, /terms and llms.txt.
 */
export const DRE_LICENSE: string | null = '01990429';


/**
 * E.164 for a US number, or null when there is nothing usable.
 *
 * ONE CONSTRUCTION, because there were two. ContactAdvisor built its links by
 * hand as `tel:${digits}` and `sms:${digits}` — no country code — while these
 * constants produced `+1`-prefixed hrefs, so the same phone rendered differently
 * depending on which component you were looking at. iOS's Contact.swift agrees
 * with the +1 form, which makes the hand-rolled pair the odd one out.
 */
export function toE164(phone: string | null | undefined): string | null {
  const digits = phone ? phone.replace(/\D/g, '') : '';
  if (digits.length === 10) return `+1${digits}`;
  // Already carries the country code.
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/** `tel:` href for a phone, or null when it is not a usable US number. */
export function telHref(phone: string | null | undefined): string | null {
  const e164 = toE164(phone);
  return e164 ? `tel:${e164}` : null;
}

/** `sms:` href for a phone, or null when it is not a usable US number. */
export function smsHref(phone: string | null | undefined): string | null {
  const e164 = toE164(phone);
  return e164 ? `sms:${e164}` : null;
}

/** `tel:` href for the phone, or `null` when no phone is configured. */
export const CONTACT_PHONE_TEL: string | null = telHref(CONTACT_PHONE);

/** `sms:` href for the phone, or `null` when no phone is configured. */
export const CONTACT_SMS: string | null = smsHref(CONTACT_PHONE);

/** `mailto:` href for the support inbox. */
export const CONTACT_EMAIL_HREF = `mailto:${CONTACT_EMAIL}`;

/** True when a real phone number is configured. Drives call/text affordances. */
export const HAS_PHONE = CONTACT_PHONE !== null;
